import MinusIcon from '@components/basics/icons/minus';
import { TracpageEditTour } from '@webapp/controllers/tracpage/tracpage-edit-tour';
import { TracpageNewTour } from '@webapp/controllers/tracpage/tracpage-new-tour';
import { useOutsideClick } from '@webapp/hooks/useOutsideClick';
import theme from '@webapp/theme';
import { rgba } from 'polished';
import React, { ComponentProps, FC } from 'react';
import { Translate } from 'react-localize-redux';
import styled, { ThemeProvider } from 'styled-components';

type EditTourProps = ComponentProps<typeof TracpageEditTour>;
type NewTourProps = ComponentProps<typeof TracpageNewTour>;
type CleanFormProps<T extends EditTourProps | NewTourProps> = Omit<
  T,
  'onCancel' | 'loading'
>;

type Props = { onCancel: () => void; loading?: boolean; isLight?: boolean } & (
  | {
      variant: 'edit';
      formProps: CleanFormProps<EditTourProps>;
    }
  | {
      variant: 'new';
      formProps: CleanFormProps<NewTourProps>;
    }
);

export const TracpageTourForm: FC<Props> = ({
  variant,
  onCancel,
  formProps,
  loading = false,
  isLight = false,
}) => {
  const ref = useOutsideClick<HTMLDivElement>(onCancel);

  return (
    <Wrapper ref={ref}>
      <div className="header">
        <h4>
          <Translate
            id={
              variant === 'new' ? 'PROFILE.EVENTS.ADD' : 'PROFILE.EVENTS.EDIT'
            }
          />
        </h4>
        <button onClick={onCancel} className="icon-button">
          <MinusIcon />
        </button>
      </div>
      <ThemeProvider theme={{ ...theme, colors: theme.variants.light }}>
        {variant === 'new' && (
          <TracpageNewTour loading={loading} isLight={isLight} {...formProps} />
        )}
        {variant === 'edit' && (
          <TracpageEditTour
            loading={loading}
            isLight={isLight}
            {...formProps}
          />
        )}
      </ThemeProvider>
    </Wrapper>
  );
};

// uses theme form @shared-tracpage
const Wrapper = styled.div`
  width: 100%;
  padding: 28px 24px;
  border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.1)};

  & > .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    & > h4 {
      margin: 0;
      font-size: 14px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.primary};
    }

    & > .icon-button {
      background-color: ${({ theme }) => rgba(theme.colors.accent, 0.3)};
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      & > svg {
        width: 18px;
        height: 18px;
        & > path {
          fill: ${({ theme }) => theme.colors.accent};
        }
      }
    }
  }
`;
